import { useEffect, useState } from "react";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { api, apiError, setToken } from "../lib/api";
import { useAuth } from "../store/AuthContext";
import { markWelcome } from "../components/onboarding/WelcomeDialog";

// /demo/<code>  or  /start?connect=…&demo=<code>
export const demoCodeFromUrl = () => {
  const m = window.location.pathname.match(/^\/demo\/([A-Za-z0-9]+)/);
  return (m && m[1]) || new URLSearchParams(window.location.search).get("demo") || "";
};

// Private demo link: signs the invited person straight in (no plan chooser), then hands over to the workspace + welcome pop-up.
export default function DemoLanding({ code, switching = false }) {
  const { user, signIn } = useAuth();
  const [err, setErr] = useState("");
  const [ended, setEnded] = useState(null); // {name, email, message, plans} — demo over: offer the plans instead of a dead end
  const [name, setName] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const info = await api.demoInfo(code); if (alive) setName(info.name);
        if (switching) { await api.logout().catch(() => {}); setToken(null); localStorage.removeItem("xpattern.previewAs"); localStorage.removeItem("xpattern.connection"); } // fresh start, like a new customer's PC // leave the current account so the demo signs in cleanly
        const r = await api.demoActivate(code);
        if (!alive) return;
        markWelcome("demo");
        signIn(r);
        const q = new URLSearchParams(window.location.search); q.delete("demo");
        const connect = q.get("connect");
        window.history.replaceState({}, "", connect ? `/start?connect=${connect}` : "/");
      } catch (e) {
        if (!alive) return;
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 410 && d?.ended) setEnded(d); else setErr(apiError(e, "This demo link is not valid."));
      }
    })();
    return () => { alive = false; };
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickPlan = (plan) => {
    const q = new URLSearchParams(window.location.search); q.delete("demo"); q.set("subscribe", plan);
    if (ended?.email) q.set("email", ended.email);
    window.location.assign(`/start?${q}`);
  };
  return (
    <div className="flex h-[100dvh] items-center justify-center bg-[#0c1017] p-6 text-[#e8e9ec]" data-testid="demo-landing">
      <div className="glass-panel w-full max-w-md p-8 text-center">
        <img src="/xpattern-x.png" alt="" className="mx-auto h-10 w-auto opacity-90" />
        {ended ? (
          <>
            <div className="mt-5 text-[16px] font-semibold" data-testid="demo-ended-title">Your demo has ended</div>
            <p className="mt-3 text-[13px] leading-relaxed text-[#c9ccd2]" data-testid="demo-ended-message">{ended.message}</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[["monthly", "Monthly", ended.plans?.monthly, "#2f80ff"], ["yearly", "Yearly · best value", ended.plans?.yearly, "#f0c945"]].map(([id, label, price, accent]) => (
                <button key={id} data-testid={`demo-ended-subscribe-${id}`} onClick={() => pickPlan(id)} className="glass-cta flex flex-col items-center px-4 py-3 text-[13px] font-semibold" style={{ "--glass-accent": accent }}>
                  <span>Subscribe {label}</span><span className="mt-0.5 text-[11px] font-normal text-[#c9ccd2]">{price}</span>
                </button>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-[#9aa0a8]">Same account, license and plotter pairing — nothing to reinstall.</p>
          </>
        ) : err ? (
          <>
            <div className="mt-5 flex items-center justify-center gap-2 text-[16px] font-semibold"><AlertTriangle className="h-5 w-5 text-[#f5a524]" /> Demo unavailable</div>
            <p className="mt-3 text-[13px] leading-relaxed text-[#c9ccd2]" data-testid="demo-error">{err}</p>
            <a href="/home" className="glass-cta mt-6 inline-flex items-center justify-center px-5 py-2.5 text-[13px] font-semibold" style={{ "--glass-accent": "#2f80ff" }}>Visit xpattern.net</a>
          </>
        ) : (
          <>
            <div className="mt-5 flex items-center justify-center gap-2 text-[16px] font-semibold"><Sparkles className="h-5 w-5 text-[#f5a524]" /> {name ? `Setting up ${name.split(" ")[0]}'s demo` : "Opening your demo"}</div>
            <p className="mt-3 text-[13px] text-[#9aa0a8]">{user ? "Unlocking the workspace…" : "One moment — signing you in with full access."}</p>
            <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-[#4c9ffe]" />
          </>
        )}
      </div>
    </div>
  );
}
export { DemoLanding };
